// What the dashboard draws, decided apart from how it draws it.
//
// Pure, so the awkward parts are testable without a browser — and every awkward
// part here is a question about *what a number means* rather than about pixels.
// The load-bearing one: **two scores are comparable only if they were measured
// on the same basis**, and a ledger routinely holds several. Drawing one line
// through them shows a cliff where the ruler changed and reads as the search
// having collapsed, which is the opposite of what happened.

import type {
  TunerCompute,
  TunerDigest,
  TunerGame,
  TunerIsland,
  TunerProfile,
  TunerPulse
} from "src/engine/net/tuner";

/**
 * Colour is never the only carrier — every badge is labelled too.
 *
 * **The loser is named, not called "stock".** A batch rotates over a panel of
 * the field's champions, so the army opposite is one of several and the card is
 * a picture of that game in particular; "stock won" beside a board another
 * champion won is the label disagreeing with the position under it. Falls
 * back where there is no panel, which is what the royale and the tournament
 * play.
 */
export function teamLabel(game: TunerGame): string {
  if (game.outcome === "candidate") return `${game.profile} won`;
  if (game.outcome === "stock") return `${game.opponent ?? "stock"} won`;
  return "drawn";
}

/**
 * One run of ledger entries measured the same way.
 *
 * A basis change is a new ruler: the goal, the batch or the versions moved, so
 * a score either side of it is a number about a different measurement. Adjacent
 * entries are grouped rather than all entries with the same basis, because a
 * basis can be returned to — two islands on different code, a rollback — and
 * bridging that gap would connect points either side of an interruption.
 */
export interface Segment {
  basis: string;
  points: {
    at: number;
    score: number;
    foeScore: number | null;
    generation: number;
    island: string;
  }[];
}

export function segmentsOf(ledger: TunerProfile["ledger"]): Segment[] {
  const out: Segment[] = [];
  for (const entry of ledger) {
    const last = out[out.length - 1];
    if (last && last.basis === entry.basis) last.points.push(entry);
    else out.push({ basis: entry.basis, points: [entry] });
  }
  return out;
}

/**
 * The part of a profile's history worth drawing, and what is being left out.
 *
 * Only the newest basis is drawn. The alternative — every point on one axis —
 * is the dual-axis mistake wearing different clothes: it puts two measurements
 * on one scale and lets the reader believe the comparison. Saying "nine earlier
 * bases, not comparable" is less chart and more information.
 */
export interface Progress {
  current: Segment | null;
  /** How many earlier, differently-measured runs are not on screen. */
  earlier: number;
  /** The best score reached within the current basis, running. */
  best: number[];
  /**
   * What the opposition scored on the same games, point for point.
   *
   * **Sparse, and the holes are the point.** The loss score was added after
   * these ledgers started, so entries written before it carry `null` — and a
   * basis change is not guaranteed to have separated them, because nothing
   * about the fitness batch moved when it was added. `null` is drawn as a gap
   * rather than as a nought, which would be a claim that the opposition scored
   * exactly level.
   */
  foe: (number | null)[];
  /** How many points in the current basis recorded one. */
  foeSeen: number;
}

export function progressOf(profile: TunerProfile): Progress {
  const segments = segmentsOf(profile.ledger);
  const current = segments.length > 0 ? segments[segments.length - 1] : null;
  const best: number[] = [];
  let running = -Infinity;
  for (const point of current?.points ?? []) {
    running = Math.max(running, point.score);
    best.push(running);
  }
  const foe = (current?.points ?? []).map((point) =>
    typeof point.foeScore === "number" && Number.isFinite(point.foeScore) ? point.foeScore : null
  );
  return {
    current,
    earlier: Math.max(0, segments.length - 1),
    best,
    foe,
    foeSeen: foe.filter((value) => value !== null).length
  };
}

/** The low and high of every series that has to share one axis. */
export interface Scale {
  low: number;
  high: number;
}

/**
 * One vertical scale across several series.
 *
 * **Because the whole content of the loss line is a comparison of
 * magnitudes.** Two lines each scaled to their own range are two pictures on
 * one grid: a loss line self-scaled to its own spread would draw a gap that is
 * not there, or hide one that is. It also fixes the pair already on the chart —
 * `best` is a running maximum of `score`, so its low is the *first* score where
 * the offers' low is the *smallest*, and the two were being drawn on rulers
 * that differed by exactly that.
 *
 * Nulls and non-finite values are skipped rather than counted as nought, for
 * the reason `progressOf` records them as holes.
 */
export function scaleOf(...series: (number | null)[][]): Scale {
  const values: number[] = [];
  for (const list of series) {
    for (const value of list) {
      if (typeof value === "number" && Number.isFinite(value)) values.push(value);
    }
  }
  if (values.length === 0) return { low: 0, high: 0 };
  return { low: Math.min(...values), high: Math.max(...values) };
}

/**
 * A series as an SVG path, on a scale it was handed rather than one of its own.
 *
 * A `path` and not a `polyline` because a sparse series has to break: a
 * polyline joins whatever points it is given, so a run of unrecorded entries
 * would be drawn as a straight line across the gap — which is an assertion
 * about measurements nobody took.
 *
 * A flat scale centres, the same reading `points` gives it: every offer scoring
 * the same is a real state and should draw level rather than vanish as `NaN`.
 */
export function pathOn(
  values: (number | null)[],
  scale: Scale,
  width: number,
  height: number
): string {
  const range = scale.high - scale.low;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  let out = "";
  let drawing = false;
  values.forEach((value, i) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      drawing = false;
      return;
    }
    const y = range === 0 ? height / 2 : height - ((value - scale.low) / range) * height;
    const x = values.length > 1 ? i * step : width / 2;
    out += `${drawing ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)} `;
    drawing = true;
  });
  return out.trim();
}

/**
 * Map values onto a box, y inverted for SVG.
 *
 * A flat series is centred rather than divided by a zero range — a search whose
 * every offer scored the same is a real state, and it should draw as a level
 * line rather than as `NaN`, which silently removes the path element.
 */
export function points(values: number[], width: number, height: number): string {
  if (values.length === 0) return "";
  const low = Math.min(...values);
  const high = Math.max(...values);
  const range = high - low;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  return values
    .map((value, i) => {
      const y = range === 0 ? height / 2 : height - ((value - low) / range) * height;
      const x = values.length > 1 ? i * step : width / 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** Where a value sits in a series, as a fraction — for placing the end marker. */
export function endPoint(values: number[], width: number, height: number): { x: number; y: number } | null {
  if (values.length === 0) return null;
  const parsed = points(values, width, height).split(" ");
  const [x, y] = parsed[parsed.length - 1].split(",").map(Number);
  return { x, y };
}

/**
 * How long ago, in words.
 *
 * The dashboard's most important number after the scores: a digest carries when
 * it was built, so a page that has not heard in an hour can say whether the
 * search is quiet or the publisher is dead. Those look identical without it.
 */
export function since(at: number, now: number): string {
  // **A timestamp nobody has is said out loud, never computed with.** Zero is
  // already what this digest means by "unknown" — `ledgerOf` writes it for a
  // line it could not parse — and arithmetic on one produces a confident
  // "20961d ago". The guard also catches a non-number arriving from the wire,
  // which is how an unparsed ISO string once printed **"NaNd ago"**.
  if (!Number.isFinite(at) || at <= 0) return "at some point";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Is the feed stale?
 *
 * **Two clocks, because there are two things that can have stopped**, and for a
 * long time the page could only see the slower one. A digest changes when a
 * *generation* lands, which under a pooled search is many minutes apart — so
 * this had to be set loose enough to sit through that, and a publisher that
 * died was invisible for the length of a generation. The pulse arrives every few
 * seconds, so where there is one, silence past half a minute is the publisher
 * being gone and nothing else.
 *
 * The digest's own age is the fallback, for a tuner running code that cannot
 * send a pulse: reporting a dead feed at a deployment that is working exactly as
 * it was built to is worse than reading a minute late.
 */
export const STALE_MS = 3 * 60 * 1000;
export const PULSE_STALE_MS = 30 * 1000;
export const isStale = (at: number, now: number): boolean => now - at > STALE_MS;

export function feedIsStale(
  pulse: TunerPulse | null,
  digest: TunerDigest | null,
  now: number
): boolean {
  if (pulse) return now - pulse.at > PULSE_STALE_MS;
  return digest !== null && isStale(digest.at, now);
}

/**
 * How long an island may play nothing before the strip stops calling it moving.
 *
 * **Deliberately not the watchdog's stall threshold**, which is ten minutes and
 * lives on the box in `tools/lib/health.mjs`. That number decides whether to
 * *bounce the container*; this one answers "is this island playing games right
 * now", which is a different question with a much shorter useful answer — an
 * island between generations is scoring, breeding and writing a ledger, and
 * saying so is the point of the strip. The page must not imply it knows when a
 * bounce is due, so it says quiet and leaves the verdict where it belongs.
 */
export const ISLAND_QUIET_MS = 2 * 60 * 1000;

/** One island, as the live strip draws it. */
export interface PulseIsland {
  id: string;
  games: number;
  /** When it last played one, in words. */
  ago: string;
  moving: boolean;
}

/** What the live strip says. */
export interface PulseView {
  /** Games played this pass, everywhere. */
  games: number;
  moving: number;
  islands: PulseIsland[];
  headline: string;
}

/**
 * The live strip.
 *
 * Reads the beats the islands write per *game* — the smallest, fastest-moving
 * thing on the volume — so the page has something true to say in the minutes
 * between one generation landing and the next.
 *
 * **A pass that is not in flight is not a stall**, and the strip has to say so
 * or a box idling out its budget between passes reads as a broken one. That is
 * the same distinction the watchdog makes with `expecting`, made here for the
 * same reason.
 */
export function pulseView(pulse: TunerPulse | null, now: number): PulseView | null {
  if (!pulse) return null;
  const islands: PulseIsland[] = pulse.islands.map((beat) => {
    // `started` counts, because an island that has just launched has played
    // nothing and is not quiet — it is loading the rules core, which is the
    // noisiest and least informative moment of a pass.
    const last = Math.max(beat.at, beat.started);
    return {
      id: beat.id,
      games: beat.games,
      ago: since(beat.at, now),
      moving: last > 0 && now - last < ISLAND_QUIET_MS
    };
  });
  const moving = islands.filter((island) => island.moving).length;
  const headline = !pulse.expecting
    ? "between passes — nothing is expected to be playing"
    : islands.length === 0
      ? "a pass is in flight and no island has reported a game"
      : `${pulse.games} games this pass · ${moving} of ${islands.length} island(s) moving`;
  return { games: pulse.games, moving, islands, headline };
}

/**
 * A duration a person reads, from milliseconds.
 *
 * Three bands because a tuner game spans three orders of magnitude — a short
 * classic opening is under a second and a four-hundred-ply padded board is
 * most of a minute — and one unit across that range is either four significant
 * figures of milliseconds or a rounding to "0.0s".
 */
export function readDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** How fast the fleet is simulating, as the panel draws it. */
export interface ComputeView {
  /** Typical game, which is the median — see `TunerCompute`. */
  typical: string;
  /** Mean game, which is what throughput is made of. */
  mean: string;
  /** Milliseconds a ply, the figure that survives games getting shorter. */
  perPly: string;
  /** Mean plies a game, the context `mean` needs. */
  plies: number;
  /** What the figures are over, in words. */
  over: string;
  /**
   * Games an hour across the fleet, or null when nothing can be inferred.
   *
   * **From the pulse, not the gallery**, and that is the whole reason it is
   * here rather than in the digest: the gallery is a *sample* — wins plus one
   * loss in eight — so a rate derived from it would report a fraction of the
   * games actually played. The pulse counts every game every island has
   * played this pass.
   */
  perHour: number | null;
}

/**
 * The compute panel.
 *
 * Two sources on two clocks, which is the split the whole page is built on: the
 * durations come from the digest, because they are read off gallery records and
 * only change when a generation lands; the throughput comes from the pulse,
 * because it is a rate and the only thing that makes a rate worth showing is
 * that it is current.
 */
export function computeView(
  compute: TunerCompute | undefined,
  pulse: TunerPulse | null,
  now: number
): ComputeView | null {
  if (!compute || compute.games === 0) return null;
  // Elapsed since the pass began, which is what `pulse.games` counts over.
  // Guarded rather than assumed: between passes `passStarted` is nought, and
  // dividing by an epoch-length elapsed would report a rate of zero as
  // confidently as a real one.
  const elapsed = pulse && pulse.passStarted > 0 ? now - pulse.passStarted : 0;
  const perHour =
    pulse && elapsed > 60_000 ? Math.round((pulse.games / elapsed) * 3_600_000) : null;
  return {
    typical: readDuration(compute.median),
    mean: readDuration(compute.ms),
    perPly: compute.msPerPly > 0 ? `${compute.msPerPly}ms` : "—",
    plies: compute.plies,
    over:
      compute.games < compute.window
        ? `last ${compute.games} games`
        : `last ${compute.window} games`,
    perHour
  };
}

/** An island that has not been written to in this long is a finished process. */
const ISLAND_IDLE_MS = 10 * 60 * 1000;
export const islandIsLive = (island: TunerIsland, now: number): boolean =>
  now - island.at < ISLAND_IDLE_MS;

/**
 * Which of the chart's series are drawn.
 *
 * A page-wide setting rather than one per card: the charts are read against
 * each other, and two cards drawing different series is a comparison between
 * two different pictures.
 */
export interface SeriesShown {
  offers: boolean;
  best: boolean;
  foe: boolean;
  pending: boolean;
}

export const ALL_SERIES: SeriesShown = { offers: true, best: true, foe: true, pending: true };

/**
 * The series names, in the order they are drawn and offered.
 *
 * A list rather than four booleans written out at every call site, so the
 * toggle row, the scale and the paths cannot disagree about what there is.
 */
export const SERIES_KEYS = ["offers", "best", "foe", "pending"] as const;
export type SeriesKey = (typeof SERIES_KEYS)[number];

/** Never all four off — an empty chart reads as a broken card, not a setting. */
export const someShown = (shown: SeriesShown): boolean =>
  SERIES_KEYS.some((key) => shown[key]);

/**
 * A stored setting, read back.
 *
 * **Per key with a default, never a cast.** This comes off `localStorage`, so
 * it may have been written before a series existed — and an absent key read as
 * `undefined` is a line that silently stops being drawn rather than one that
 * defaults to on. Same `??` chain `cameraPrefs` states, and the same reason.
 *
 * A stored setting with everything off is repaired rather than honoured: it
 * cannot be reached through the toggles, so it can only come from an edited
 * store or a key this build has renamed, and an empty chart is not a state
 * worth restoring somebody into.
 */
export function readSeries(raw: string | null): SeriesShown {
  let stored: Partial<Record<SeriesKey, unknown>> = {};
  try {
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    if (parsed && typeof parsed === "object") stored = parsed as Partial<Record<SeriesKey, unknown>>;
  } catch {
    // Somebody else's key, or a half-written one. The defaults are fine.
  }
  const out = { ...ALL_SERIES };
  for (const key of SERIES_KEYS) {
    if (typeof stored[key] === "boolean") out[key] = stored[key];
  }
  return someShown(out) ? out : { ...ALL_SERIES };
}

/**
 * Where the search is *now*, as against where its record is.
 *
 * **The ledger is only the records.** An island appends when a generation beats
 * its own running best (`island.mjs`), so a flat line means "nothing has broken
 * the record" and says nothing about whether the search is still scoring near
 * it or has fallen off a cliff. Every generation's score *is* recorded, in each
 * island's `history.json`, and it already ships in the digest as
 * `islands[].points` — where until now nothing drew it.
 *
 * So this is the newest generation among the islands still running, which is
 * the number that becomes the next point on the line if it beats the best.
 *
 * **Not an in-flight candidate**, and there is no such thing to have: a
 * candidate's fitness does not exist until its whole batch has been played and
 * scored, and the only thing that moves during one is a count of games. This is
 * the finest-grained score the box actually produces.
 */
export interface Pending {
  score: number;
  /** Which island is there. */
  island: string;
  /** The generation that scored it. */
  generation: number;
  /** Above the best the ledger has recorded on this basis — a higher scorer. */
  ahead: boolean;
}

export function pendingOf(
  profile: TunerProfile,
  best: number[],
  now: number
): Pending | null {
  const record = best[best.length - 1];
  let found: Pending | null = null;
  for (const island of profile.islands) {
    if (!islandIsLive(island, now)) continue;
    // The newest generation this island finished. `points` is oldest first and
    // holds every generation, record-breaking or not.
    const last = island.points[island.points.length - 1];
    if (!last || !Number.isFinite(last.score)) continue;
    if (found && last.score <= found.score) continue;
    found = {
      score: last.score,
      island: island.id,
      generation: last.generation,
      ahead: typeof record === "number" ? last.score > record : true
    };
  }
  return found;
}

/** What the gallery is showing, and what it could show. */
export interface GalleryFilter {
  profile: string | null;
  winsOnly: boolean;
}

export function filterGallery(games: TunerGame[], filter: GalleryFilter): TunerGame[] {
  return games.filter((game) => {
    if (filter.profile && game.profile !== filter.profile) return false;
    if (filter.winsOnly && game.outcome !== "candidate") return false;
    return true;
  });
}

/** Every profile that has a game in the gallery, for the filter row. */
export function profilesInGallery(games: TunerGame[]): string[] {
  return [...new Set(games.map((game) => game.profile))].sort();
}

/**
 * A one-line account of a game, under its boards.
 *
 * The board and the ply cap belong together: `playing` at 128 plies is a game
 * that was adjudicated rather than won, and reading the status without the
 * length makes an adjudication look like a stalemate.
 */
export function gameCaption(game: TunerGame): string {
  const board = game.board === "classic" ? "classic board" : `${game.board} board`;
  const ended = game.status === "playing" ? `adjudicated at ${game.plies} plies` : `${game.status} · ${game.plies} plies`;
  return `${board} · ${ended} · seed ${game.seed}`;
}

/** Totals for the header, so the page says what it is showing at a glance. */
export function summaryOf(digest: TunerDigest | null): {
  profiles: number;
  games: number;
  wins: number;
  generations: number;
} {
  if (!digest) return { profiles: 0, games: 0, wins: 0, generations: 0 };
  // Counted through `repeats`, not as cards. Identical games are folded into
  // one card to keep the feed readable, but they were really played — a count
  // of cards would quietly under-report the search by however much it was
  // repeating itself, which is the one thing the fold must not hide.
  const played = (game: TunerGame): number => Math.max(1, game.repeats);
  return {
    profiles: digest.profiles.length,
    games: digest.gallery.reduce((sum, game) => sum + played(game), 0),
    wins: digest.gallery
      .filter((game) => game.outcome === "candidate")
      .reduce((sum, game) => sum + played(game), 0),
    generations: digest.profiles.reduce((sum, profile) => sum + profile.generation, 0)
  };
}

